<template>
  <div class="content-card">
    <div class="content-title">我的评论</div>
    <el-card class="comments-card">
      <!-- 评论状态子页签 -->
      <el-tabs
          v-model="activeCommentTab"
          @tab-click="handleCommentTabChange"
          class="comment-tabs"
      >
        <el-tab-pane label="全部" name="all">
          <template #label>
            全部
            <span v-if="commentCounts.total" class="tab-badge">{{ commentCounts.total }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane label="待审核" name="pending">
          <template #label>
            待审核
            <span v-if="commentCounts.pending" class="tab-badge">{{ commentCounts.pending }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane label="审核通过" name="approved">
          <template #label>
            审核通过
            <span v-if="commentCounts.approved" class="tab-badge">{{ commentCounts.approved }}</span>
          </template>
        </el-tab-pane>
        <el-tab-pane label="审核拒绝" name="rejected">
          <template #label>
            审核拒绝
            <span v-if="commentCounts.rejected" class="tab-badge">{{ commentCounts.rejected }}</span>
          </template>
        </el-tab-pane>
      </el-tabs>

      <!-- 评论列表 -->
      <div class="comments-list" v-loading="loadingComments">
        <div v-if="comments.length === 0 && !loadingComments" class="no-comments">
          暂无评论数据
        </div>

        <div class="comment-item" v-for="comment in comments" :key="comment.id">
          <div class="comment-header">
            <span class="comment-post-title">
              帖子：{{ comment.postTitle || '未知帖子' }}
            </span>
            <span class="comment-time">
              {{ formatDateTime(comment.createTime) }}
            </span>
          </div>
          <div class="comment-content">
            {{ comment.content }}
          </div>
          <div class="comment-footer">
            <span class="comment-status" :class="'status-' + comment.auditStatus">
              {{ getAuditStatusText(comment.auditStatus) }}
            </span>
            <el-button
                type="text"
                size="mini"
                class="delete-btn"
                @click="handleDeleteComment(comment.id)"
            >
              删除
            </el-button>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div style="display: flex; justify-content: center; width: 100%;">
        <el-pagination
            background
            layout="prev, pager, next, total"
            :current-page="pageNum"
            :page-size="pageSize"
            :total="total"
            @size-change="handlePageSizeChange"
            @current-change="handlePageChange"
            style="text-align: center; margin-top: 10rem; font-size: 2rem"
        ></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import newService from "@/api/newService";

export default {
  name: "MyComments",
  data() {
    return {
      activeCommentTab: "all", // 默认显示全部
      comments: [], // 评论列表数据
      loadingComments: false, // 评论加载状态
      // 分页参数
      pageNum: 1,
      pageSize: 10,
      total: 0,
      // 各状态评论数量统计
      commentCounts: {
        pending: 0,    // 待审核
        approved: 0,   // 审核通过
        rejected: 0,   // 审核拒绝
        total: 0       // 总数
      }
    };
  },
  mounted() {
    this.loadComments();
  },
  methods: {
    // 处理删除评论
    handleDeleteComment(commentId) {
      this.$confirm('确定要删除这条评论吗？', '删除确认', {
        confirmButtonText: '确定',
        confirmButtonClass: "el-button--danger",
        cancelButtonText: "取消",
        customClass: "logout-confirm-modal",
        type: 'warning'
      }).then(() => {
        this.deleteCommentToUser(commentId);
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消删除'
        });
      });
    },

    // 调用API删除评论
    deleteCommentToUser(commentId) {
      newService.deleteCommentToUser(commentId).then(() => {
        this.$message({
          type: 'success',
          message: '评论删除成功'
        });
        // 重新加载评论列表
        this.loadComments();
      }).catch(err => {
        console.error('删除评论失败:', err);
        this.$message.error('删除评论失败，请重试');
      });
    },

    loadComments() {
      this.loadingComments = true;
      const userId = this?.$TOOL?.data?.get('USER_INFO').id;

      // 根据当前活跃的标签确定审核状态
      let auditStatus = null;
      switch(this.activeCommentTab) {
        case 'pending':
          auditStatus = 0; // 待审核
          break;
        case 'approved':
          auditStatus = 1; // 审核通过
          break;
        case 'rejected':
          auditStatus = 2; // 审核拒绝
          break;
        default:
          auditStatus = null; // 全部
      }

      // 调用接口获取评论数据
      newService.getCommentToUser({
        userId,
        auditStatus,
        pageNum: this.pageNum,
        pageSize: this.pageSize
      }).then(res => {
        this.comments = res.records || [];
        this.total = res.total || 0;

        // 更新数量统计
        if (res.counts) {
          this.commentCounts = {
            pending: res.counts.pending || 0,
            approved: res.counts.approved || 0,
            rejected: res.counts.rejected || 0,
            total: res.counts.total || 0
          };
        }
      }).catch(err => {
        console.error('获取评论失败:', err);
        this.$message.error('获取评论失败，请重试');
      }).finally(() => {
        this.loadingComments = false;
      });
    },

    // 评论标签切换
    handleCommentTabChange(tab) {
      this.activeCommentTab = tab.props.name;
      this.pageNum = 1; // 切换标签时重置页码
      this.loadComments();
    },

    // 分页大小改变
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.pageNum = 1;
      this.loadComments();
    },

    // 页码改变
    handlePageChange(page) {
      this.pageNum = page;
      this.loadComments();
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // 获取审核状态文本
    getAuditStatusText(status) {
      switch(status) {
        case 0: return '待审核';
        case 1: return '审核通过';
        case 2: return '审核拒绝';
        default: return '未知状态';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* 强制修改弹窗样式，提高优先级 */
.el-message-box.logout-confirm-modal {
  width: 600px !important;
  min-height: 300px !important;
}

/* 标题样式 */
.el-message-box.logout-confirm-modal .el-message-box__title {
  font-size: 24px !important;
  margin-bottom: 20px !important;
}

/* 提示内容样式 */
.el-message-box.logout-confirm-modal .el-message-box__content {
  font-size: 20px !important;
  padding: 30px 20px !important;
  line-height: 1.8 !important;
}

/* 按钮区域样式 */
.el-message-box.logout-confirm-modal .el-message-box__btns {
  padding: 20px !important;
}

/* 按钮文字样式 */
.el-message-box.logout-confirm-modal .el-button {
  font-size: 18px !important;
  padding: 10px 25px !important;
  margin: 0 10px !important;
}

.content-card {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 0.5rem;
  box-shadow: 0 0.2rem 1rem rgba(0, 0, 0, 0.05);
  padding: 3rem;
  margin-bottom: 5rem;
}

.content-title {
  font-size: 2.4rem;
  color: #333333;
  font-weight: 500;
  margin-bottom: 3rem;
  padding-bottom: 1rem;
  border-bottom: 0.1rem solid #E5E5E5;
}

.comments-card {
  border: none;
  box-shadow: none;
  padding: 1rem 0;
}

.comment-tabs {
  margin-bottom: 2rem;

  ::v-deep .el-tabs__item {
    font-size: 1.6rem;
    padding: 0 1.5rem;
  }

  ::v-deep .el-tabs__active-bar {
    background-color: #FF0B41;
  }

  ::v-deep .el-tabs__item.is-active {
    color: #FF0B41;
  }
}

.tab-badge {
  display: inline-block;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  background-color: #FF0B41;
  color: white;
  font-size: 12px;
  border-radius: 50%;
  margin-left: 6px;
  vertical-align: middle;
}

.comments-list {
  background-color: #f9f9f9;
  border-radius: 0.8rem;
  padding: 2rem;
  min-height: 30rem;
}

.comment-item {
  background-color: white;
  border-radius: 0.6rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.2rem 0.5rem rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.4rem 1rem rgba(0, 0, 0, 0.08);
  }
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 2rem;
  color: #666;
}

.comment-post-title {
  font-weight: 500;
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comment-time {
  color: #999;
}

.comment-content {
  font-size: 2rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px dashed #eee;
  word-break: break-all;
}

.comment-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 2rem;

  .delete-btn {
    color: #FF0B41;
    font-size: 2rem;
    padding: 0 0.5rem;

    &:hover {
      color: #e60a3a;
      text-decoration: underline;
    }

    &:disabled {
      color: #ccc;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
}

.comment-status {
  padding: 0.3rem 1rem;
  border-radius: 1.2rem;
  font-weight: 500;
}

.status-0 {
  background-color: #fff3cd;
  color: #856404;
}

.status-1 {
  background-color: #d4edda;
  color: #155724;
}

.status-2 {
  background-color: #f8d7da;
  color: #721c24;
}

.reject-reason {
  color: #721c24;
  font-size: 1.3rem;
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-comments {
  text-align: center;
  padding: 8rem 0;
  color: #999;
  font-size: 1.8rem;
  background-size: 10rem 10rem;
}

// 分页样式调整
::v-deep .el-pagination.is-background .el-pager li {
  margin: 0 5px;
  border-radius: 6px;
}

::v-deep .el-pagination.is-background .el-pager li:not(.disabled).is-active {
  background: #FFEDEF;
  color: #FF0B41;
  border-radius: 6px;
}

::v-deep .el-pagination__total {
  margin-right: 10px;
  color: #333;
}

::v-deep .el-pagination button {
  border-radius: 6px;
  color: #666;
}

::v-deep .el-pagination button:hover {
  color: #FF0B41;
  border-color: #FF0B41;
}
</style>
